import { demandSettingsTarget } from "./device-settings-target.js";
import { experienceCommand, confirmExperienceCommand } from "./device-experience.js";

export function supportsMuteProfile(device) {
  return ["SCROLLER_CYD_28_TOUCH", "SCROLLER_CYD_28_ST7789_TOUCH", "SCROLLER_CYD_28_ST7789_SECURE", "SCROLLER_CYD_35_TOUCH"].includes(device?.profileId ?? device?.hardwareProfile);
}

function verifySnapshot(target, value) {
  demandSettingsTarget(target, value);
  if (value.contractVersion !== 1 || !Number.isSafeInteger(value.version) || value.version < 0) throw new Error("Refresh this Pirdy's settings before changing sound.");
  return value;
}

export async function readCloudMute({ target, read, isCurrent }) {
  if (!isCurrent()) throw new Error("The Pirdy account changed.");
  const snapshot = verifySnapshot(target, await read(target.deviceId));
  if (!isCurrent()) throw new Error("The Pirdy account changed.");
  return snapshot;
}

// Only explicit commands read the current version and queue a change. There is
// no timer, retry or acknowledgement polling; refresh is another user action.
export async function sendCloudMute({ target, muted, read, save, isCurrent, requestId = crypto.randomUUID() }) {
  if (typeof muted !== "boolean") throw new Error("Choose Mute or Unmute.");
  const before = await readCloudMute({ target, read, isCurrent });
  if (before.pending) throw new Error("This Pirdy has a pending settings change. Check its sound status before retrying.");
  const values = experienceCommand(before.reportedState, "mute", { muted });
  if (!isCurrent()) throw new Error("The Pirdy account changed.");
  const snapshot = verifySnapshot(target, await save(target.deviceId, { requestId, expectedVersion: before.version, values }));
  if (!isCurrent()) throw new Error("The Pirdy account changed.");
  const command = snapshot.pending ?? snapshot.lastCommand;
  if (!command || command.commandId !== requestId || command.version !== before.version + 1 ||
      snapshot.version !== command.version || command.values?.action !== "mute" || command.values?.muted !== muted) throw new Error("The sound command was not confirmed. Check this Pirdy's sound status before retrying.");
  demandSettingsTarget(target, command);
  if (!snapshot.pending) {
    if (snapshot.completedCommandId !== requestId || snapshot.lastError) throw new Error("Pirdy has not confirmed this sound change. Check its sound status before retrying.");
    confirmExperienceCommand(before.reportedState, values, snapshot.reportedState);
  }
  return snapshot;
}

export function muteStatus(snapshot) {
  if (snapshot?.pending?.values?.action === "mute" && typeof snapshot.pending.values.muted === "boolean") return { known:false, pending:true, message:snapshot.pending.values.muted ? "Mute requested" : "Unmute requested" };
  if (snapshot?.pending) return { known:false, pending:true, message:"Settings pending" };
  const state = snapshot?.reportedState;
  if (snapshot?.lastError) return { known:false, pending:false, message:"Sound change unconfirmed" };
  if (state?.features?.audioMute !== true || state.speakerAvailable !== true || typeof state.muted !== "boolean" ||
      !Number.isInteger(state.volume) || state.volume < 0 || state.volume > 3 || state.muted !== (state.volume === 0) ||
      !Number.isInteger(state.unmuteVolume) || state.unmuteVolume < 1 || state.unmuteVolume > 3) return { known:false, pending:false, message:"Sound status unknown" };
  return { known:true, muted:state.muted, pending:false, message:state.muted ? "Muted" : "Unmuted" };
}

export async function sendCloudMuteBatch({ targets, muted, read, save, isCurrent, onSnapshot = () => {} }) {
  const unique = new Map();
  for (const target of targets) {
    const key = String(target?.deviceId ?? "").toUpperCase();
    if (unique.has(key)) demandSettingsTarget(unique.get(key), target);
    else { demandSettingsTarget(target, target);unique.set(key, target); }
  }
  const results = [];
  for (const target of unique.values()) {
    if (!isCurrent()) throw new Error("The Pirdy account changed.");
    if (!supportsMuteProfile(target)) { results.push({ target, unsupported:true });continue; }
    try {
      const snapshot = await sendCloudMute({ target, muted, read, save, isCurrent });
      onSnapshot(target, snapshot);results.push({ target, snapshot });
    } catch (error) {
      if (!isCurrent()) throw error;
      results.push({ target, error });
    }
  }
  return results;
}
